<template>
  <div>
    <!-- Wallets Dashboard Section -->
    <div class="dashboard-section">
      <div class="dashboard-header">
        <h2>Wallets Dashboard</h2>
        <a-button
          type="primary"
          @click="$router.push('/master-wallet-details')"
          style="background: #26a246; border-color: #26a246"
          icon="arrow-right"
        >
          View Master Wallets
        </a-button>
      </div>

      <!-- Dashboard Cards -->
      <div class="cards-container">
        <div
          class="card-wrapper"
          v-for="(card, index) in dashboardCards"
          :key="index"
        >
          <div class="card-content">
            <div class="card-header">
              <div>
                <h3>{{ card.title }}</h3>
                <p>{{ card.description }}</p>
              </div>
              <div
                class="icon-wrapper"
                :style="{ backgroundColor: card.iconBg, color: card.iconColor }"
              >
                <a-icon :class="['fa', card.icon]" />
              </div>
            </div>
            <p class="card-paragraph">
              <span
                v-if="card.spanIcon || card.span"
                :style="{
                  color:
                    card.spanIcon === 'fa-arrow-down' ? '#ef4444' : '#10b981',
                  display: 'inline-flex',
                  alignItems: 'center',
                }"
              >
                <i
                  :class="['fa', card.spanIcon]"
                  style="font-size: 14px; margin-right: 4px"
                ></i>
                {{ card.span }}
              </span>
              {{ card.content }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div style="margin-top: 40px">
      <a-row :gutter="16">
        <a-col :span="6">
          <p style="margin: 0px">Search</p>
          <a-input
            v-model="search"
            placeholder="Search by owner name"
            style="width: 100%"
            allow-clear
            prefix-icon="search"
          />
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Status</p>
          <a-select
            v-model="filters.status"
            placeholder="Filter by Status"
            allowClear
            style="width: 100%"
          >
            <a-select-option value="active">Active</a-select-option>
            <a-select-option value="inactive">Inactive</a-select-option>
          </a-select>
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Wallet Type</p>
          <a-select
            v-model="filters.type"
            placeholder="Filter by Type"
            allowClear
            style="width: 100%"
          >
            <a-select-option value="seller">Seller</a-select-option>
            <a-select-option value="buyer">Buyer</a-select-option>
          </a-select>
        </a-col>
      </a-row>
    </div>

    <!-- Wallets Overview Table -->
    <div class="table-section">
      <h3>Wallets Overview</h3>
      <a-table
        :columns="walletColumns"
        :data-source="tableData"
        row-key="id"
        :pagination="tablePagination"
        :scroll="{ x: 'max-content' }"
        :loading="tableLoading"
        style="background-color: #fafafa !important"
        :rowClassName="() => 'custom-table-row'"
        @change="handleTableChange"
      >
        <template slot="ownerName" slot-scope="text">
          {{ text }}
        </template>
        <template slot="walletType" slot-scope="text">
          <span style="font-weight: 500">{{ text }}</span>
        </template>
        <template slot="balance" slot-scope="text, record">
          {{ record.currency }} {{ text }}
        </template>
        <template slot="kycStatus" slot-scope="text">
          <StatusTag :status="text" />
        </template>
        <template slot="status" slot-scope="text, record">
          <StatusTag :status="record.ownerStatus" />
        </template>
        <template slot="actions" slot-scope="text, record">
          <a-button
            type="link"
            icon="eye"
            style="cursor: pointer; color: #6b7280"
            @click="() => viewWallet(record)"
          />
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import StatusTag from "../shared-components/StatusTag.vue";
import sellersService from "../../api/sellers.service";
import FinancingAndPaymentsService from "../../api/financing-and-payments.service";
import { formatAmount } from "../../utils";

export default {
  name: "WalletsTab",
  components: {
    StatusTag,
  },
  data() {
    return {
      // Search and filters
      search: "",
      filters: {
        status: undefined,
        type: undefined,
      },
      searchDebounceTimer: null,

      // Pagination
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },

      // Table data and loading
      tableData: [],
      tableLoading: false,

      dashboardCards: [
        {
          title: "Total Funds Held",
          description: "-",
          icon: "fa-shield-alt",
          iconColor: "#8b5cf6",
          iconBg: "#ede9fe",
          content: "Total funds across all wallets (Disbursement + Collection)"
        },
        {
          title: "Disbursement Balance",
          description: "-",
          icon: "fa-wallet",
          iconColor: "#3b82f6",
          iconBg: "#dbeafe",
          content: "Available wallet balance for disbursements"
        },
        {
          title: "Collection Balance",
          description: "-",
          icon: "fa-credit-card",
          iconColor: "#10b981",
          iconBg: "#d1fae5",
          content: "Wallet balance for payments collected from buyer wallets"
        },
        {
          title: "Manual Repayments",
          description: "-",
          icon: "fa-hand-holding-usd",
          iconColor: "#26a246",
          iconBg: "#ecfdf5",
          content: "Manually logged buyer payments"
        }
      ],
      walletColumns: [
        {
          title: "Wallet ID",
          dataIndex: "walletId",
          key: "walletId",
          width: 200,
        },
        {
          title: "Owner Name",
          dataIndex: "ownerName",
          key: "ownerName",
          scopedSlots: { customRender: "ownerName" },
          width: 200,
        },
        {
          title: "Email",
          dataIndex: "email",
          key: "email",
          width: 200,
        },
        {
          title: "Type",
          dataIndex: "walletType",
          key: "walletType",
          scopedSlots: { customRender: "walletType" },
          width: 120,
        },
        {
          title: "Balance",
          dataIndex: "balance",
          key: "balance",
          scopedSlots: { customRender: "balance" },
          width: 150,
        },
        {
          title: "KYC Status",
          dataIndex: "kycStatus",
          key: "kycStatus",
          scopedSlots: { customRender: "kycStatus" },
          width: 120,
        },
        {
          title: "Status",
          dataIndex: "ownerStatus",
          key: "ownerStatus",
          scopedSlots: { customRender: "status" },
          width: 120,
        },
        {
          title: "Actions",
          key: "actions",
          scopedSlots: { customRender: "actions" },
          width: 100,
        },
      ],
    };
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        total: this.pagination.totalCount,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: false,
      };
    },
  },
  mounted() {
    this.fetchWallets();
    this.fetchWalletDetails();
  },
  watch: {
    "pagination.pageIndex"() {
      this.fetchWallets();
    },
    search() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.pageIndex = 1;
        this.fetchWallets();
      }, 400);
    },
    filters: {
      handler() {
        this.pagination.pageIndex = 1;
        this.fetchWallets();
      },
      deep: true,
    },
  },
  methods: {
    async fetchWallets() {
      this.tableLoading = true;

      
        const params = {
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
          status: this.filters.status || "",
          tagType: this.filters.type || "",
          search: this.search || "",
        };

        const response = await sellersService.getPagedWallets(params);

        if (response.status === 200 || response.data?.status === 200) {
          const data = response.data?.data;
          console.log("API Response:", data);

          // Map the API response to flatten wallets - only include "Seller" or "Buyer" wallets
          const flattenedData = [];
          (data.results || []).forEach((item) => {
            if (item.wallets && item.wallets.length > 0) {
              // Filter wallets to only include "Seller" or "Buyer" types
              item.wallets.forEach((wallet) => {
                // Only process "Seller" or "Buyer" wallets
                if (wallet.name === "Seller" || wallet.name === "Buyer") {
                  flattenedData.push({
                    id: item.id,
                    walletId: wallet.code || wallet.id,
                    ownerName: item.firstName + " " + item.lastName,
                    walletType: wallet.name || "N/A",
                    balance: wallet.balance || "0.00",
                    currency: wallet.currency || "KES",
                    walletProperty: wallet.property || "N/A",
                    isDefault: wallet.default === 1,
                    ownerStatus: item.status || "Active",
                    phone: item.phone || "N/A",
                    email: item.email || "N/A",
                    nationalId: item.nationalId || "N/A",
                    kycStatus: item.kycStatus || "N/A",
                    creditScore: item.creditScore || null,
                    thirdPartyId: item.thirdPartyId || "N/A",
                    dob: item.dob || "N/A",
                    gender: item.gender || "N/A",
                    createdAt: item.createdAt,
                    tags: item.tags || [],
                    metadata: item.metadata || "N/A",
                    banks: item.banks || [],
                    code: item.code || "N/A",
                  });
                }
              });
            }
            // Remove the else block since we don't want to show owners without Seller/Buyer wallets
          });

          this.tableData = flattenedData;

          this.pagination.totalCount = data.totalCount || 0;
          console.log("Mapped table data:", this.tableData);
        } else {
          console.log("API Error:", response);
          this.$message.error(response.data.message)
          this.tableData = [];
          this.pagination.totalCount = 0;
        }
        this.tableLoading = false;
    },

    async fetchWalletDetails() {
      try {
        const response = await FinancingAndPaymentsService.getWalletStats();
        
        if (response.data?.code === 200) {
          const data = response.data.data;
          
          // Update dashboard cards with real data
          this.dashboardCards[0].description = formatAmount(data.totalFundsHeld || 0);
          this.dashboardCards[1].description = formatAmount(data.disbursementBalance || 0);
          this.dashboardCards[2].description = formatAmount(data.collectionBalance || 0);
          this.dashboardCards[3].description = formatAmount(data.manualRepaymentTotal || 0);
        } else {
          this.$message.warning("Failed to fetch wallet details");
        }
      } catch (error) {
        console.error("Error fetching wallet details:", error);
        this.$message.error("Failed to load wallet details");
      }
    },

    handleTableChange(pagination) {
      if (pagination.current !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.current;
      }
    },

    viewWallet(record) {
      console.log("Viewing wallet:", record);
      // Navigate to wallet details page
      this.$router.push({
        name: "WalletDetails",
        params: { walletId: record.id },
      });
    },
  },
};
</script>

<style scoped>
.dashboard-section {
  margin-bottom: 32px;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.dashboard-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.time-period-selector {
  display: flex;
  align-items: center;
}

.cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: flex-start;
  width: 100%;
}

.card-wrapper {
  flex: 0 0 calc(25% - 12px);
  max-width: calc(25% - 12px);
  min-width: 200px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
  transition: all 0.3s ease;
}

.card-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 120px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  background-color: transparent;
  padding: 0;
  border-bottom: none;
}

.card-header p {
  font-weight: 700;
  font-size: 18px;
  color: #000000;
  margin: 0;
}

.card-header h3 {
  font-size: 0.9em;
  color: #6b7280;
  margin: 0 0 4px 0;
}

.card-paragraph {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  margin: 0;
  line-height: 1.4;
  margin-top: auto;
  color: #6b7280;
}

.icon-wrapper {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
  flex-shrink: 0;
}

.table-section {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.table-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 1200px) {
  .card-wrapper {
    flex: 0 0 calc(50% - 8px);
    max-width: calc(50% - 8px);
  }
}

@media (max-width: 576px) {
  .card-wrapper {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}
</style>
